const parseISODate = (dateStr) => {
	if (!dateStr) return null;
	const [year, month, day] = String(dateStr)
		.split("-")
		.map((part) => Number(part));
	if (!year || !month || !day) return null;
	return new Date(year, month - 1, day);
};

export const toISODate = (dateObj) => {
	if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return null;
	const year = dateObj.getFullYear();
	const month = String(dateObj.getMonth() + 1).padStart(2, "0");
	const day = String(dateObj.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const getMonday = (dateObj) => {
	const monday = new Date(dateObj);
	monday.setHours(0, 0, 0, 0);
	monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
	return monday;
};

export const getDefaultDatedDay = (days, todayIso) => {
	let firstDatedDay = null;
	let latestDay = null;
	for (const day of days) {
		if (!day.date) continue;
		if (!firstDatedDay || day.date < firstDatedDay.date) {
			firstDatedDay = day;
		}
		if (
			day.date <= todayIso &&
			(!latestDay || day.date > latestDay.date)
		) {
			latestDay = day;
		}
	}
	return latestDay || firstDatedDay || days[0] || null;
};

// Rilascio automatico (`auto_publish_weeks`), gemello della scadenza
// automatica: una settimana datata compare da sola il lunedì del suo primo
// giorno meno l'anticipo (1 = la domenica prima, il default). Calcolato dalle
// date, mai salvato: stessa aritmetica di backend/services/week_release.py.
const LEAD_DAYS_DEFAULT = 1;
const LEAD_DAYS_MIN = 0;
const LEAD_DAYS_MAX = 7;

export const getAutoPublishLeadDays = (programItem) => {
	const raw = programItem?.auto_publish_lead_days;
	const value = raw === undefined || raw === null ? NaN : Number(raw);
	if (!Number.isFinite(value)) return LEAD_DAYS_DEFAULT;
	return Math.max(LEAD_DAYS_MIN, Math.min(LEAD_DAYS_MAX, Math.trunc(value)));
};

// Assente = spento: il comportamento storico (manuale). I non datati restano
// fuori: la loro ancora giusta è la data di assegnazione per atleta.
export const isAutoPublishEnabled = (programItem) =>
	programItem?.auto_publish_weeks === true && programItem?.is_dated !== false;

/**
 * Il giorno in cui la settimana compare da sola, o null (programma manuale,
 * settimana senza giorni datati). Non guarda `hold`: la data serve anche a
 * dire di una settimana trattenuta quando iniziano gli allenamenti.
 */
export const getAutoReleaseDate = (programItem, week) => {
	if (!isAutoPublishEnabled(programItem)) return null;
	const dayDates = (week?.days || [])
		.map((day) => parseISODate(day?.date))
		.filter(Boolean)
		.sort((a, b) => a - b);
	if (dayDates.length === 0) return null;
	const release = getMonday(dayDates[0]);
	release.setDate(release.getDate() - getAutoPublishLeadDays(programItem));
	return toISODate(release);
};

const isAutoReleasedOn = (programItem, week, todayIso) => {
	if (week?.hold === true) return false;
	const release = getAutoReleaseDate(programItem, week);
	return Boolean(release) && release <= todayIso;
};

const isDatedWeekPast = (week, todayIso) => {
	const dayDates = (week?.days || [])
		.map((day) => parseISODate(day?.date))
		.filter(Boolean)
		.sort((a, b) => a - b);
	if (dayDates.length === 0) return false;

	const todayParsed = parseISODate(todayIso);
	if (!todayParsed) return false;

	const currentMonday = getMonday(todayParsed);
	return dayDates[dayDates.length - 1] < currentMonday;
};

const inferCurrentUndatedWeekNumber = (programItem, todayIso) => {
	const todayParsed = parseISODate(todayIso);
	if (!todayParsed) return null;

	const inferredStarts = (programItem?.weeks || [])
		.map((week) => {
			const publishDate = parseISODate(week?.publish_date);
			if (!publishDate) return null;
			const weekNumber = Number(week?.week_number || 1);
			const inferredStart = getMonday(publishDate);
			inferredStart.setDate(inferredStart.getDate() - (weekNumber - 1) * 7);
			return inferredStart;
		})
		.filter(Boolean)
		.sort((a, b) => a - b);

	let anchorMonday = inferredStarts[0] || null;
	if (!anchorMonday && programItem?.created_at) {
		const createdAt = new Date(programItem.created_at);
		if (!Number.isNaN(createdAt.getTime())) {
			anchorMonday = getMonday(createdAt);
		}
	}

	if (!anchorMonday) return null;

	const todayMonday = getMonday(todayParsed);
	const deltaDays = Math.floor((todayMonday - anchorMonday) / 86400000);
	if (deltaDays < 0) return 1;
	return Math.floor(deltaDays / 7) + 1;
};

export const isWeekPastForAthlete = (programItem, week, todayIso) => {
	const manualDisableDate = parseISODate(week?.disable_date);
	const todayParsed = parseISODate(todayIso);
	if (manualDisableDate && todayParsed) {
		return todayParsed >= manualDisableDate;
	}

	if (programItem?.auto_disable_weeks === false) {
		return false;
	}
	// Reactivated by the coach: automatic expiry no longer applies.
	if (week?.keep_visible === true) {
		return false;
	}

	if (programItem?.is_dated !== false) {
		return isDatedWeekPast(week, todayIso);
	}

	const currentWeekNumber = inferCurrentUndatedWeekNumber(programItem, todayIso);
	if (currentWeekNumber === null) return false;

	const weekNumber = Number(week?.week_number || 1);
	return weekNumber < currentWeekNumber;
};

export const isWeekVisibleForAthlete = (
	programItem,
	week,
	todayIso,
	athleteId,
) => {
	if (isWeekPastForAthlete(programItem, week, todayIso)) {
		return false;
	}

	if (week?.is_published === true) {
		return true;
	}

	if (week?.publish_date && week.publish_date <= todayIso) {
		return true;
	}

	if (isAutoReleasedOn(programItem, week, todayIso)) {
		return true;
	}

	if (!athleteId) return false;
	const previewAthleteIds = Array.isArray(week?.preview_athlete_ids)
		? week.preview_athlete_ids
		: [];
	return previewAthleteIds.includes(athleteId);
};

/**
 * Total number of days in a program.
 *
 * Counts weeks[].days, which is the canonical source: the legacy top-level
 * `days` is empty on programs that have weeks, and `/programs?compact=true`
 * does not project it at all. The compact projection does include
 * `weeks.days.date`, so the day arrays keep their real length there.
 */
export const getProgramDayCount = (programItem) => {
	const weeks = Array.isArray(programItem?.weeks) ? programItem.weeks : [];
	if (weeks.length > 0) {
		return weeks.reduce(
			(total, week) =>
				total + (Array.isArray(week?.days) ? week.days.length : 0),
			0,
		);
	}
	return Array.isArray(programItem?.days) ? programItem.days.length : 0;
};

export const getProgramVisibilityStats = (
	programItem,
	athleteId,
	todayIso = toISODate(new Date()),
) => {
	if (!todayIso) {
		return { hasVisibleWeeks: false, visibleWeeks: 0, totalWeeks: 0 };
	}

	if (!Array.isArray(programItem?.weeks) || programItem.weeks.length === 0) {
		const compactVisibleWeeks = Number(programItem?.active_weeks_count || 0);
		if (compactVisibleWeeks > 0) {
			return {
				hasVisibleWeeks: true,
				visibleWeeks: compactVisibleWeeks,
				totalWeeks: Number(programItem?.weeks_count || compactVisibleWeeks),
			};
		}
	}

	const weeks = Array.isArray(programItem?.weeks) ? programItem.weeks : [];
	if (weeks.length === 0) {
		const hasLegacyDays = Array.isArray(programItem?.days)
			? programItem.days.length > 0
			: false;
		return {
			hasVisibleWeeks: hasLegacyDays,
			visibleWeeks: hasLegacyDays ? 1 : 0,
			totalWeeks: hasLegacyDays ? 1 : 0,
		};
	}

	const visibleWeeks = weeks.filter((week) =>
		isWeekVisibleForAthlete(programItem, week, todayIso, athleteId),
	).length;

	return {
		hasVisibleWeeks: visibleWeeks > 0,
		visibleWeeks,
		totalWeeks: weeks.length,
	};
};

export const buildAthletePreviewProgram = (
	programItem,
	{ todayIso = toISODate(new Date()), athleteId } = {},
) => {
	if (!programItem) return null;
	const weeks = Array.isArray(programItem.weeks) ? programItem.weeks : [];
	if (weeks.length === 0) {
		return programItem;
	}

	const visibleWeeks = todayIso
		? weeks.filter((week) =>
				isWeekVisibleForAthlete(programItem, week, todayIso, athleteId),
			)
		: [];

	return {
		...programItem,
		weeks: visibleWeeks,
	};
};
